// src/axiosConfig.js

import axios from "axios";

let isRefreshing = false;
let failedQueue = [];

// Global variables to store context values
let globalToken = null;
let globalGraphData = null;
let globalSetIsTokenFetched = null;
let globalSetIsCMDBTokenFetched = null;
let globalSetIsOnboardingTokenFetched = null;
// Function to set global context values from the React context
export const setGlobalContextValues = (token, graphData, setIsTokenFetched, setIsCMDBTokenFetched=null, setIsOnboardingTokenFetched=null) => {
  globalToken = token;
  globalGraphData = graphData;
  globalSetIsTokenFetched = setIsTokenFetched;
  globalSetIsCMDBTokenFetched = setIsCMDBTokenFetched;
  globalSetIsOnboardingTokenFetched = setIsOnboardingTokenFetched;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

const refreshToken = async () => {
  if (!globalToken || !globalGraphData?.mail) {
    throw new Error("Required context values not available for token refresh");
  }

  const response = await axios.post(
    `${process.env.REACT_APP_BACKEND_URI}/api/auth/getToken`,
    {
      userEmail: globalGraphData.mail
    },
    {
      headers: {

        Authorization: `Bearer ${globalToken}`
      },
      // Avoid interceptor recursion
      skipInterceptor: true
    }
  );

  const newToken = response.data.data;

  if (!newToken) {
    throw new Error("No token received from refresh endpoint");
  }

  localStorage.setItem("jwt_token", newToken);

  // Update the token fetched state if available
  if (globalSetIsTokenFetched) {
    globalSetIsTokenFetched(true);
  }

  return newToken;
};

// const refreshCMDBToken = async () => {
//   if (!globalToken || !globalGraphData?.mail) {
//     throw new Error("Required context values not available for CMDB token refresh");
//   }

//   const response = await axios.post(
//     `${process.env.REACT_APP_CMDB_BACKEND_URI}/getToken`,
//     {
//       userEmail: globalGraphData.mail
//     },
//     {
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${globalToken}`
//       },
//       // Avoid interceptor recursion
//       skipInterceptor: true
//     }
//   );

//   const newToken = response.data.data;

//   if (!newToken) {
//     throw new Error("No token received from CMDB refresh endpoint");
//   }

//   localStorage.setItem("cmdb_token", newToken);

//   // Update the token fetched state if available
//   if (globalSetIsCMDBTokenFetched) {
//     globalSetIsCMDBTokenFetched(true);
//   }

//   return newToken;
// };

const refreshOnboardingToken = async () => {
  try {
    const currentToken = localStorage.getItem("jwt_token");
    
    if (!currentToken) {
      throw new Error("JWT token not available for onboarding token refresh");
    }

    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URI}/api/auth/getOnboardingToken`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`
        },
        // Avoid interceptor recursion
        skipInterceptor: true
      }
    );

    if (response.status !== 200 || response.data.status !== "success") {
      throw new Error("Failed to refresh onboarding token");
    }

    const newToken = response.data.token;

    if (!newToken) {
      throw new Error("No token received from onboarding refresh endpoint");
    }

    localStorage.setItem("azure_token", newToken);

    // Update the token fetched state if available
    if (globalSetIsOnboardingTokenFetched) {
      globalSetIsOnboardingTokenFetched(true);
    }

    return newToken;
  } catch (error) {
    console.error("Error refreshing onboarding token:", error);
    throw error;
  }
};

axios.interceptors.request.use((config) => {
  // Skip interceptor for refresh requests to avoid recursion
  if (config.skipInterceptor) {
    delete config.skipInterceptor;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // Skip interceptor for refresh requests to avoid recursion
    if (originalRequest.skipInterceptor) {
      return Promise.reject(error);
    }

    // Get the URL to determine which token to refresh
    const requestUrl = originalRequest.url;
    
    // Check for "Access Denied" message from backend or 401 unauthorized
    const isAccessDenied =
      error.response?.data.detail === "Access Denied" ||
      error.response?.data === "Access Denied" ||
      error.response?.status === 401;

    if (isAccessDenied && !originalRequest._retry) {
      // Determine which token needs to be refreshed based on the URL
      let refreshFunction;
      let tokenType;

      // if (requestUrl.includes(process.env.REACT_APP_CMDB_BACKEND_URI)) {
      //   refreshFunction = refreshCMDBToken;
      //   tokenType = "cmdb";
      // } 
      if (requestUrl.includes(process.env.REACT_APP_ONBOARDING_URI)) {
        refreshFunction = refreshOnboardingToken;
        tokenType = "onboarding";
      } else {
        refreshFunction = refreshToken;
        tokenType = "jwt";
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshFunction();

        processQueue(null, newToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        console.error(`${tokenType.toUpperCase()} token refresh failed`, refreshError);

        // Clear the specific token on refresh failure
        localStorage.removeItem(`${tokenType}_token`);



        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
